/* eslint-disable no-magic-numbers */
import React from 'react';
import styled, { css } from 'styled-components';
import { withCssSelector } from '../../shared/withCssSelector';
import {
	FormInputSize,
	formInputSizeDefinitions,
	formInputSizes
} from '../../utils/formInputSize';
import { transition } from '../../themes/mainTheme';
import { BoxedFieldProps, boxedFieldStates } from '../../shared/boxedField';

// Text sits on this ratio in every boxed field; the file button's padding is
// derived from it, so the button lands on the field's own inset.
const lineHeightRatio = 1.25;

const StyledInput = styled.input<{ $size: FormInputSize } & BoxedFieldProps>`
	font-family: ${props => props.theme.font};
	accent-color: ${props => props.theme.form.input.accentColor};
	// Native date pickers, number spinners, autofill and scrollbars follow this.
	color-scheme: dark;
	pointer-events: ${props => props.$disabled ? 'none' : 'auto'};

	background-image: none;
	border: ${props => props.theme.form.border.width}px
		solid ${props => props.theme.form.input.border};
	${props => props.$hasError && css`
		border-color: ${props.theme.form.input.error.border};
		box-shadow: ${props.theme.form.input.error.shadow};
	`}
	border-radius: ${props => props.theme.form.border.radius}px;

	transition: ${transition('border-color', 'box-shadow')};

	${({ $disabled, theme }) => $disabled && css`
		opacity: ${theme.form.disabled.opacity};
	`}

	&[type='radio'],
	&[type='range'],
	&[type='checkbox'] {	
		height: 16px;
		width: 16px;
		cursor: pointer;
		border-color: ${props => props.theme.form.input.border};

		:focus {
			border-color: ${props => props.theme.form.input.focus.border};
			outline: 0;
			box-shadow: ${props => props.theme.form.input.focus.shadow};
		}
	}

	&[type='range'] {
		display: block;
		width: 100%;
	}

	// Text-entry types share the boxed field that SearchInput and the dropdown
	// trigger draw: same stroke, same fill, same states. The three sit in the
	// same form, so they have to read as one control at rest.
	&[type='time'],
	&[type='date'],
	&[type='text'],
	&[type='datetime'],
	&[type='datetime-local'],
	&[type='email'],
	&[type='file'],
	&[type='month'],
	&[type='week'],
	&[type='url'],
	&[type='number'],
	&[type='password'],
	&[type='search'],
	&[type='submit'],
	&[type='tel'] {
		display: block;
		width: 100%;
		box-sizing: border-box;
		line-height: ${lineHeightRatio};
		color: ${props => props.theme.form.input.color};
		background-color: ${props => props.theme.form.input.background};
		background-image: none;

		${({ $size }) => {
		const size = formInputSizeDefinitions[$size];
		return css`
				height: ${size.height}px;
				padding: 0 ${size.paddingHorizontal}px;
				font-size: ${size.fontSize}px;
			`;
	}}

		::placeholder {
			color: ${props => props.theme.form.input.placeholderColor};
		}

		// Stroke and states — shared with SearchInput and the multi-select trigger.
		${boxedFieldStates}
	}

	// A file field holds a button and a filename, not text. Left native, the
	// button keeps the browser's grey chrome and sits flush against the name;
	// here it wears the dimmed button look and the box lays them out as a row.
	&[type='file'] {
		display: flex;
		align-items: center;
		cursor: pointer;

		${({ $size, theme }) => {
		const size = formInputSizeDefinitions[$size];
		return css`
				// The button is inset by the field's own padding on every side, so it
				// starts where a sibling field's text starts and sits square in the box.
				padding: ${size.paddingHorizontal}px;

				// No height: the button sizes to its label, padded so the space above
				// and below it equals the inset on its left. A fixed height would
				// have to be re-derived every time the type scale moves.
				::file-selector-button {
					margin-right: ${size.gap}px;
					padding: ${(size.height - 2 * size.paddingHorizontal
		- lineHeightRatio * size.fontSize) / 2}px 12px;
					font: inherit;
					color: ${theme.text.secondary};
					background: ${theme.fill.dimmed};
					border: 0;
					border-radius: ${theme.form.border.radius}px;
					cursor: pointer;
					transition: ${transition('background', 'color')};
				}

				::file-selector-button:hover {
					color: ${theme.text.primary};
					background: ${theme.fill.dimmedSecondary};
				}
			`;
	}}
	}
`;

export type InputProps = {
	hasError?: boolean | undefined,
	size?: FormInputSize | undefined
} & Omit<React.ComponentPropsWithoutRef<'input'>, 'size'>

export const Input = withCssSelector(React.forwardRef<HTMLInputElement, InputProps>(
	function Input(
		{
			hasError,
			disabled,
			type = 'text',
			tabIndex,
			readOnly,
			size = formInputSizes.compact,
			...props
		},
		ref
	) {
		return <StyledInput
			ref={ref}
			type={type}
			$hasError={hasError}
			$disabled={disabled}
			$size={size}
			tabIndex={disabled ? -1 : tabIndex}
			readOnly={disabled ? true : readOnly}
			{...props}
		/>;
	}
), StyledInput.toString());
